/**
 * Formatting for figures that arrive as decimal strings.
 *
 * The API sends money and hours as strings on purpose: Postgres sums NUMERIC
 * exactly, and the moment a value becomes a JavaScript number that exactness is
 * gone. `8504060.82` survives the round trip, but the habit of parsing money into
 * a float is how a total ends up a cent out and nobody can say where.
 *
 * So these functions format the string directly - grouping digits, rounding by
 * inspecting characters - and never call parseFloat on a monetary value.
 *
 * Chart geometry is the deliberate exception, and it is confined to `toPlotValue`
 * below, where the output is a pixel coordinate rather than an amount.
 */

interface DecimalParts {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
}

function parseDecimal(value: string): DecimalParts {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  return { negative, whole: whole === '' ? '0' : whole, fraction };
}

/** Round a decimal string half-up at `places`, carrying through the integer part. */
function roundDecimal(value: string, places: number): DecimalParts {
  const { negative, whole, fraction } = parseDecimal(value);

  if (fraction.length <= places) {
    return { negative, whole, fraction: fraction.padEnd(places, '0') };
  }

  const kept = fraction.slice(0, places);
  const nextDigit = fraction.charCodeAt(places) - 48;

  if (nextDigit < 5) {
    return { negative, whole, fraction: kept };
  }

  // Carry by hand rather than via arithmetic, so a value larger than
  // Number.MAX_SAFE_INTEGER still rounds correctly.
  //
  // Built with a loop rather than a spread: the input is ASCII digits, but
  // spreading a string yields code points and the habit is wrong for text that
  // might not be.
  const source = whole + kept;
  const digits: string[] = new Array<string>(source.length);
  for (let position = 0; position < source.length; position += 1) {
    digits[position] = source.charAt(position);
  }
  let index = digits.length - 1;
  let carry = 1;
  while (index >= 0 && carry === 1) {
    const digit = (digits[index]?.charCodeAt(0) ?? 48) - 48 + carry;
    digits[index] = String(digit % 10);
    carry = digit >= 10 ? 1 : 0;
    index -= 1;
  }
  const carried = (carry === 1 ? '1' : '') + digits.join('');
  const splitAt = carried.length - places;

  return {
    negative,
    whole: carried.slice(0, splitAt) || '0',
    fraction: carried.slice(splitAt),
  };
}

function group(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `8504060.8231` becomes `$8,504,060.82`. */
export function money(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = roundDecimal(value, 2);
  const stripped = whole.replace(/^0+(?=\d)/, '');
  return `${negative ? '-' : ''}$${group(stripped)}.${fraction}`;
}

/**
 * Format a stored decimal without rounding or a currency symbol.
 * `1.2650` stays `1.2650`; `39.5000` stays `39.5000`.
 */
export function decimalExact(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = parseDecimal(value);
  const stripped = whole.replace(/^0+(?=\d)/, '');
  const body = fraction === '' ? group(stripped) : `${group(stripped)}.${fraction}`;
  return `${negative ? '-' : ''}${body}`;
}

/** Compact form for headline figures: `$8.50M`. Full precision stays in the title. */
export function moneyCompact(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole } = roundDecimal(value, 2);
  const stripped = whole.replace(/^0+(?=\d)/, '');
  const sign = negative ? '-' : '';

  if (stripped.length > 6) {
    // Millions. Insert the decimal point by slicing, not by dividing.
    const millions = stripped.slice(0, stripped.length - 6);
    const remainder = stripped.slice(stripped.length - 6, stripped.length - 4);
    return `${sign}$${group(millions)}.${remainder}M`;
  }
  if (stripped.length > 3) {
    const thousands = stripped.slice(0, stripped.length - 3);
    const remainder = stripped.slice(stripped.length - 3, stripped.length - 2);
    return `${sign}$${group(thousands)}.${remainder}k`;
  }
  return `${sign}$${stripped}`;
}

/** `1234.5000` becomes `1,234.5`. Trailing zeroes dropped; hours are not currency. */
export function hours(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = roundDecimal(value, 2);
  const stripped = whole.replace(/^0+(?=\d)/, '');
  const tidy = fraction.replace(/0+$/, '');
  return `${negative ? '-' : ''}${group(stripped)}${tidy === '' ? '' : `.${tidy}`}`;
}

/** `56.0087` becomes `$56.01`, for per-hour rates where cents matter. */
export function rate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const { negative, whole, fraction } = roundDecimal(value, 2);
  return `${negative ? '-' : ''}$${whole.replace(/^0+(?=\d)/, '')}.${fraction}`;
}

/**
 * Margin as a percentage, computed from two decimal strings.
 *
 * Uses BigInt rather than division on floats: the inputs are exact and the output
 * is a display value with one decimal place, so there is no reason to introduce
 * rounding error in between.
 */
export function marginPercent(grossProfit: string, billed: string): string {
  const toScaled = (value: string): bigint => {
    const { negative, whole, fraction } = roundDecimal(value, 4);
    const magnitude = BigInt(whole + fraction);
    return negative ? -magnitude : magnitude;
  };
  const numerator = toScaled(grossProfit);
  const denominator = toScaled(billed);
  if (denominator === 0n) return '—';

  const tenths = (numerator * 1000n) / denominator;
  const sign = tenths < 0n ? '-' : '';
  const absolute = tenths < 0n ? -tenths : tenths;
  return `${sign}${String(absolute / 10n)}.${String(absolute % 10n)}%`;
}

/**
 * Convert a decimal string to a number, for chart geometry only.
 *
 * Named so that its purpose is unmistakable at the call site. A pixel coordinate
 * does not need to be exact to the cent; a total does. Never use this for a figure
 * that will be displayed as an amount.
 */
export function toPlotValue(value: string): number {
  return Number(value);
}

/** `2026-03-13` becomes `03/13/2026`. Dates are civil strings, never Date objects. */
export function shortDate(iso: string): string {
  const [year = '2026', month = '01', day = '01'] = iso.split('-');
  return `${month}/${day}/${year}`;
}

const COMMISSION_SCALE = 10000n;

function toScaledBigInt(value: string): bigint {
  const { negative, whole, fraction } = roundDecimal(value, 4);
  const magnitude = BigInt(whole + fraction);
  return negative ? -magnitude : magnitude;
}

function fromScaledBigInt(value: bigint): string {
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  const wholePart = abs / COMMISSION_SCALE;
  const fracPart = abs % COMMISSION_SCALE;
  const raw = `${sign}${wholePart.toString()}.${fracPart.toString().padStart(4, '0')}`;
  const r = roundDecimal(raw, 2);
  return `${r.negative ? '-' : ''}${r.whole}.${r.fraction}`;
}

/**
 * Compute commission from tier brackets and total gross profit.
 *
 * Marginal mode applies each tier's rate only to the portion of GP within that
 * bracket (like progressive tax brackets). Flat mode applies the highest
 * qualifying tier's rate to the entire GP.
 *
 * All arithmetic uses BigInt so monetary values stay exact.
 */
export function computeCommission(
  tiers: readonly { threshold: string; rate: string; tierMode: string }[],
  totalGp: string,
): string {
  if (tiers.length === 0) return '0.00';

  const gpScaled = toScaledBigInt(totalGp);
  if (gpScaled <= 0n) return '0.00';

  const tierMode = tiers[0]?.tierMode ?? 'marginal';

  if (tierMode !== 'marginal') {
    let applicableRate = 0n;
    for (const tier of tiers) {
      if (gpScaled >= toScaledBigInt(tier.threshold)) {
        applicableRate = toScaledBigInt(tier.rate);
      }
    }
    return fromScaledBigInt((gpScaled * applicableRate) / COMMISSION_SCALE);
  }

  let total = 0n;
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (tier === undefined) break;
    const threshold = toScaledBigInt(tier.threshold);
    if (gpScaled <= threshold) break;

    const nextTier = i + 1 < tiers.length ? tiers[i + 1] : undefined;
    const nextThreshold = nextTier !== undefined ? toScaledBigInt(nextTier.threshold) : null;
    const bracketTop =
      nextThreshold !== null && gpScaled > nextThreshold ? nextThreshold : gpScaled;
    total += ((bracketTop - threshold) * toScaledBigInt(tier.rate)) / COMMISSION_SCALE;
  }

  return fromScaledBigInt(total);
}

/**
 * Find the next tier above the current GP and return the gap amount and rate.
 * Returns null when the recruiter is already at or above the highest tier.
 */
export function nextTierGap(
  tiers: readonly { threshold: string; rate: string }[],
  totalGp: string,
): { gap: string; rate: string } | null {
  const gpScaled = toScaledBigInt(totalGp);
  for (const tier of tiers) {
    const threshold = toScaledBigInt(tier.threshold);
    if (threshold > gpScaled) {
      return { gap: fromScaledBigInt(threshold - gpScaled), rate: tier.rate };
    }
  }
  return null;
}

/**
 * Returns the recruiter's progress within their current commission bracket:
 * what percentage of the way they are to the next rate increase. Returns null
 * when the recruiter is already at or above the highest tier.
 */
export function tierProgress(
  tiers: readonly { threshold: string; rate: string }[],
  totalGp: string,
): { pct: number; label: string; currentRate: string; nextRate: string | null } | null {
  if (tiers.length === 0) return null;
  const gpScaled = toScaledBigInt(totalGp);
  if (gpScaled <= 0n) return null;

  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (tier === undefined) continue;
    const nextTier = i + 1 < tiers.length ? tiers[i + 1] : undefined;

    if (nextTier === undefined) {
      return null;
    }

    const nextThreshold = toScaledBigInt(nextTier.threshold);

    if (gpScaled < nextThreshold) {
      const currentThreshold = toScaledBigInt(tier.threshold);
      const bracketSize = nextThreshold - currentThreshold;
      const progress = gpScaled - currentThreshold;
      const pct = bracketSize > 0n ? Number((progress * 100n) / bracketSize) : 0;
      const rateDisplay = `${(Number(nextTier.rate) * 100).toFixed(0)}%`;
      return {
        pct,
        label: `${String(pct)}% to ${rateDisplay} tier`,
        currentRate: tier.rate,
        nextRate: nextTier.rate,
      };
    }
  }

  return null;
}
